import { buildCanonicalVpPerformanceSettings, calculateVPPerformance } from '../shared-logic';
import type {
  OperationalAlert,
  OperationalControlReadModel,
  OperationalInstanceRow,
  OperationalStatus
} from './types';

export type ExecutiveMetricKey =
  | 'planProgress'
  | 'kpiCompliance'
  | 'kpiCoverage'
  | 'weightedPerformance'
  | 'operationalScore'
  | 'monthlyCapture'
  | 'stale'
  | 'onboarding'
  | 'noConfiguration'
  | 'noObligation'
  | 'notApplicable'
  | 'technicalError';

export const EXECUTIVE_METRIC_CONTRACT: Record<ExecutiveMetricKey, string> = {
  planProgress: 'Avance oficial del plan vivo. Solo participa cuando existe una meta de plan mayor que cero.',
  kpiCompliance: 'Cumplimiento prorrateado de KPI para el periodo. No es cobertura de configuracion.',
  kpiCoverage: 'Proporcion de KPI configurados que tienen meta. No representa cumplimiento.',
  weightedPerformance: 'Resultado canonico de calculateVPPerformance con prorrateo de dimensiones aplicables.',
  operationalScore: 'Salud de disciplina operativa: integra desempeno y penalizacion stale; no sustituye al ponderado.',
  monthlyCapture: 'Captura valida del periodo entre instancias con obligacion operativa aplicable.',
  stale: 'Meses operativos cerrados con obligacion vencida y sin captura valida.',
  onboarding: 'Instancia en incorporacion operativa protegida de penalizacion critica.',
  noConfiguration: 'La configuracion necesaria no esta completa; no equivale a error tecnico ni a cero.',
  noObligation: 'El periodo o instancia no tiene obligacion aplicable; se excluye del denominador.',
  notApplicable: 'La dimension no participa en el calculo; se muestra N/A y se excluye del promedio.',
  technicalError: 'La lectura remota fallo. Nunca se representa como cero, N/A ni una coleccion vacia.'
};

export type ExecutiveInstanceRow = OperationalInstanceRow & {
  planProgress: number | null;
  applicableKpiCompliance: number | null;
  weightedPerformance: number | null;
  captureApplicable: boolean;
};

export type ExecutiveInstanceCounts = {
  total: number;
  updated: number;
  stale: number;
  exception: number;
};

export type ExecutiveUpdateCategory = 'UPDATED' | 'STALE' | 'EXCEPTION';

const EXECUTIVE_EXCEPTION_STATUSES = new Set<OperationalStatus>([
  'ONBOARDING',
  'NOT_STARTED',
  'SIN_CONFIGURACION',
  'SIN_OBLIGACION',
  'SIN_META_VENCIDA',
  'SIN_DATOS'
]);

export const classifyExecutiveUpdateCategory = (
  row: Pick<OperationalInstanceRow, 'operationalStatus' | 'staleMonthsOperational'>
): ExecutiveUpdateCategory => {
  if (EXECUTIVE_EXCEPTION_STATUSES.has(row.operationalStatus)) return 'EXCEPTION';
  if (row.staleMonthsOperational > 0) return 'STALE';
  return 'UPDATED';
};

export const buildExecutiveInstanceCounts = (
  rows: Array<Pick<OperationalInstanceRow, 'operationalStatus' | 'staleMonthsOperational'>>
): ExecutiveInstanceCounts => rows.reduce<ExecutiveInstanceCounts>((counts, row) => {
  counts.total += 1;
  const category = classifyExecutiveUpdateCategory(row);
  if (category === 'EXCEPTION') counts.exception += 1;
  else if (category === 'STALE') counts.stale += 1;
  else counts.updated += 1;
  return counts;
}, { total: 0, updated: 0, stale: 0, exception: 0 });

export type ExecutiveVPRow = {
  vpId: string | null;
  vpName: string;
  presidentName: string;
  instances: ExecutiveInstanceRow[];
  instanceCount: number;
  indicatorDisplay?: OperationalInstanceRow['indicatorDisplay'];
  instanceCounts: ExecutiveInstanceCounts;
  planProgress: number | null;
  kpiCompliance: number | null;
  weightedPerformance: number | null;
  capturePct: number | null;
  captureCount: number;
  captureApplicableCount: number;
  criticalCount: number;
  attentionCount: number;
  alertCount: number;
  staleCount: number;
  status: OperationalStatus;
};

export type ExecutiveAlert = OperationalAlert & {
  instanceId: string;
  instanceName: string;
  vpName: string;
};

export type ExecutiveSummaryModel = {
  national: {
    healthScore: number | null;
    evaluatedVicepresidencies: number;
    visibleVicepresidencies: number;
    visibleInstances: number;
    activePlans: number;
    planProgress: number | null;
    kpiCompliance: number | null;
    weightedPerformance: number | null;
    capturePct: number | null;
    captureCount: number;
    captureApplicableCount: number;
    criticalInstances: number;
    attentionInstances: number;
    onboardingInstances: number;
    noConfigurationInstances: number;
    noObligationInstances: number;
    staleInstances: number;
  };
  vicePresidencies: ExecutiveVPRow[];
  topAlerts: ExecutiveAlert[];
  topAdvances: ExecutiveInstanceRow[];
  interventionVicepresidencies: ExecutiveVPRow[];
  legitimateExceptions: ExecutiveInstanceRow[];
  pendingCapture: ExecutiveInstanceRow[];
};

const EXCLUDED_CAPTURE_STATUSES = new Set<OperationalStatus>([
  'SIN_OBLIGACION',
  'SIN_CONFIGURACION',
  'SIN_META_VENCIDA',
  'NOT_STARTED',
  'ONBOARDING'
]);

const STATUS_PRIORITY: Record<OperationalStatus, number> = {
  CRITICO: 90,
  ATENCION: 80,
  SIN_DATOS: 70,
  ONBOARDING: 60,
  NOT_STARTED: 50,
  SIN_CONFIGURACION: 40,
  SIN_META_VENCIDA: 30,
  SIN_OBLIGACION: 20,
  AL_DIA: 10
};

const SEVERITY_PRIORITY: Record<OperationalAlert['severity'], number> = {
  critical: 4,
  high: 3,
  medium: 2,
  info: 1
};

// El Resumen Ejecutivo solo representa unidades adscritas a la estructura.
// DEMO y cualquier futura instancia sin VP permanecen disponibles en sus
// módulos operativos, pero no crean una vicepresidencia artificial.
export const isExecutiveReportInstance = (
  row: Pick<OperationalInstanceRow, 'vpId'>
): boolean => Boolean(row.vpId);

const roundPct = (value: number): number => Math.max(0, Math.min(100, Math.round(value)));

const canonicalSettings = (data: OperationalControlReadModel) => ({
  weight_dimension_kpis: data.opSettings?.kpiWeight ?? 70,
  weight_dimension_plans: data.opSettings?.planWeight ?? 30,
  weight_comisiones: data.opSettings?.weightComisiones ?? 50,
  weight_comites: data.opSettings?.weightComites ?? 30,
  weight_consejerias: data.opSettings?.weightConsejerias ?? 20
});

const toCanonicalInstance = (row: OperationalInstanceRow) => ({
  id: row.instanceId,
  name: row.instanceName,
  type: row.instanceType,
  status: 'ACTIVE',
  active: true,
  planEligible: row.planGoal > 0,
  planGoal: row.planGoal,
  planProgress: row.planPct,
  kpiEligible: row.kpiWithGoal > 0 && row.kpiCompliance !== null,
  activeKpis: row.kpiWithGoal,
  kpiCompliance: row.kpiCompliance
});

const toCanonicalVpInstance = (row: OperationalInstanceRow) => {
  const canonicalKpi = Object.prototype.hasOwnProperty.call(row, 'canonicalVpKpiCompliance')
    ? row.canonicalVpKpiCompliance ?? null
    : row.kpiCompliance;
  return {
    ...toCanonicalInstance(row),
    kpiEligible: row.kpiWithGoal > 0 && canonicalKpi !== null,
    kpiCompliance: canonicalKpi
  };
};

const canonicalPerformance = (
  rows: OperationalInstanceRow[],
  data: OperationalControlReadModel
): { global: number | null; plan: number | null; kpi: number | null } => {
  const result = calculateVPPerformance(rows.map(toCanonicalInstance), canonicalSettings(data));
  return {
    global: typeof result.global === 'number' ? result.global : null,
    plan: typeof result.plan === 'number' ? result.plan : null,
    kpi: typeof result.kpi === 'number' ? result.kpi : null
  };
};

const canonicalVpPerformance = (
  rows: OperationalInstanceRow[],
  data: OperationalControlReadModel
): { global: number | null; plan: number | null; kpi: number | null } => {
  const instances = rows.map(toCanonicalVpInstance);
  const result = calculateVPPerformance(
    instances,
    buildCanonicalVpPerformanceSettings(instances, canonicalSettings(data))
  );
  return {
    global: typeof result.global === 'number' ? result.global : null,
    plan: typeof result.plan === 'number' ? result.plan : null,
    kpi: typeof result.kpi === 'number' ? result.kpi : null
  };
};

export const isCaptureApplicable = (row: OperationalInstanceRow): boolean =>
  row.currentMonthCaptureObligation && row.resolvedWindow.isOperationallyActive && !EXCLUDED_CAPTURE_STATUSES.has(row.operationalStatus);

const getGroupStatus = (rows: OperationalInstanceRow[]): OperationalStatus => {
  if (rows.length === 0) return 'SIN_CONFIGURACION';
  return rows.reduce((worst, row) =>
    STATUS_PRIORITY[row.operationalStatus] > STATUS_PRIORITY[worst] ? row.operationalStatus : worst,
  'AL_DIA' as OperationalStatus);
};

const enrichInstance = (
  row: OperationalInstanceRow,
  data: OperationalControlReadModel
): ExecutiveInstanceRow => {
  const performance = canonicalPerformance([row], data);
  const captureApplicable = isCaptureApplicable(row);
  return {
    ...row,
    alerts: captureApplicable ? row.alerts : row.alerts.filter((alert) => alert.code !== 'NO_CURRENT_MONTH_CAPTURE'),
    planProgress: row.planGoal > 0 ? row.planPct : null,
    applicableKpiCompliance: row.kpiWithGoal > 0 ? row.kpiCompliance : null,
    weightedPerformance: performance.global,
    captureApplicable
  };
};

const buildVPRow = (
  vp: { id: string | null; name: string; presidentName: string },
  rows: OperationalInstanceRow[],
  data: OperationalControlReadModel
): ExecutiveVPRow => {
  const performance = canonicalPerformance(rows, data);
  const vpPerformance = canonicalVpPerformance(rows, data);
  const instances = rows.map((row) => enrichInstance(row, data));
  const captureApplicable = instances.filter((row) => row.captureApplicable);
  const captureCount = captureApplicable.filter((row) => row.currentMonthCapture).length;
  const displays = rows.map((row) => row.indicatorDisplay);
  const periods = new Set(displays.map((display) => display?.closedPeriod));
  const closed = periods.size === 1 && displays.every(Boolean) ? vpPerformance.kpi : null;
  const monthly = displays.every(Boolean) ? calculateVPPerformance(rows.map((row) => ({
    ...toCanonicalInstance(row),
    kpiEligible: row.indicatorDisplay?.monthlyValue != null,
    kpiCompliance: row.indicatorDisplay?.monthlyValue ?? null
  })), canonicalSettings(data)).kpi : null;
  const reasons = [...new Set(displays.map((display) => display?.monthlyReason).filter(Boolean))];
  const indicatorDisplay = {
    monthlyValue: typeof monthly === 'number' ? monthly : null,
    monthlyPeriod: displays[0]?.monthlyPeriod ?? `${data.selectedMonth} ${data.year}`,
    monthlyReason: rows.length === 0 ? 'Sin indicadores configurados' : reasons.join(' · ') || 'Sin información del mes',
    closedValue: typeof closed === 'number' ? closed : null,
    closedActiveCount: displays.reduce((sum, display) => sum + (display?.closedActiveCount ?? 0), 0),
    closedPeriod: periods.size === 1 ? displays[0]?.closedPeriod ?? 'Sin periodo cerrado canónico' : 'Periodos distintos: sin consolidar'
  };
  return {
    vpId: vp.id,
    vpName: vp.name,
    presidentName: vp.presidentName,
    instances,
    instanceCount: instances.length,
    indicatorDisplay,
    instanceCounts: buildExecutiveInstanceCounts(instances),
    planProgress: performance.plan,
    kpiCompliance: performance.kpi,
    weightedPerformance: vpPerformance.global,
    capturePct: captureApplicable.length ? roundPct((captureCount / captureApplicable.length) * 100) : null,
    captureCount,
    captureApplicableCount: captureApplicable.length,
    criticalCount: rows.filter((row) => row.operationalStatus === 'CRITICO').length,
    attentionCount: rows.filter((row) => row.operationalStatus === 'ATENCION').length,
    alertCount: instances.reduce((total, row) => total + row.alerts.length, 0),
    staleCount: rows.filter((row) => row.staleMonthsOperational > 0).length,
    status: getGroupStatus(rows)
  };
};

export const buildExecutiveSummaryModel = (data: OperationalControlReadModel): ExecutiveSummaryModel => {
  const executiveRows = data.rows.filter(isExecutiveReportInstance);
  const executiveInstanceIds = new Set(executiveRows.map((row) => row.instanceId));
  const rowsByVp = new Map<string, OperationalInstanceRow[]>();
  executiveRows.forEach((row) => {
    const key = row.vpId as string;
    rowsByVp.set(key, [...(rowsByVp.get(key) || []), row]);
  });

  const vicePresidencies = data.scopeVicepresidencies.map((vp) =>
    buildVPRow(
      { id: vp.id, name: vp.name, presidentName: vp.presidentName },
      rowsByVp.get(vp.id) || [],
      data
    )
  );
  vicePresidencies.sort((left, right) =>
    right.criticalCount - left.criticalCount ||
    right.attentionCount - left.attentionCount ||
    right.staleCount - left.staleCount ||
    left.vpName.localeCompare(right.vpName, 'es')
  );

  const allInstances = vicePresidencies.flatMap((vp) => vp.instances);
  const captureApplicable = allInstances.filter((row) => row.captureApplicable);
  const captureCount = captureApplicable.filter((row) => row.currentMonthCapture).length;
  const nationalPerformance = canonicalPerformance(executiveRows, data);

  const captureObligationIds = new Set(executiveRows.filter((row) => row.currentMonthCaptureObligation).map((row) => row.instanceId));
  const topAlerts = data.alerts
    .filter((alert) => executiveInstanceIds.has(alert.instanceId))
    .filter((alert) => alert.code !== 'NO_CURRENT_MONTH_CAPTURE' || captureObligationIds.has(alert.instanceId))
    .slice()
    .sort((left, right) =>
      SEVERITY_PRIORITY[right.severity] - SEVERITY_PRIORITY[left.severity] ||
      left.vpName.localeCompare(right.vpName, 'es') ||
      left.instanceName.localeCompare(right.instanceName, 'es')
    )
    .slice(0, 5);

  const topAdvances = allInstances
    .filter((row) => row.weightedPerformance !== null)
    .sort((left, right) =>
      (right.weightedPerformance ?? -1) - (left.weightedPerformance ?? -1) ||
      left.instanceName.localeCompare(right.instanceName, 'es')
    )
    .slice(0, 5);

  return {
    national: {
      healthScore: executiveRows.some((row) => row.operationalScore !== null) ? data.summary.healthScore : null,
      evaluatedVicepresidencies: vicePresidencies.filter((vp) => vp.weightedPerformance !== null).length,
      visibleVicepresidencies: vicePresidencies.length,
      visibleInstances: allInstances.length,
      activePlans: executiveRows.filter((row) => row.planGoal > 0).length,
      planProgress: nationalPerformance.plan,
      kpiCompliance: nationalPerformance.kpi,
      weightedPerformance: nationalPerformance.global,
      capturePct: captureApplicable.length ? roundPct((captureCount / captureApplicable.length) * 100) : null,
      captureCount,
      captureApplicableCount: captureApplicable.length,
      criticalInstances: executiveRows.filter((row) => row.operationalStatus === 'CRITICO').length,
      attentionInstances: executiveRows.filter((row) => row.operationalStatus === 'ATENCION').length,
      onboardingInstances: executiveRows.filter((row) => row.operationalStatus === 'ONBOARDING').length,
      noConfigurationInstances: executiveRows.filter((row) => row.operationalStatus === 'SIN_CONFIGURACION').length,
      noObligationInstances: executiveRows.filter((row) =>
        row.operationalStatus === 'SIN_OBLIGACION' || row.operationalStatus === 'SIN_META_VENCIDA'
      ).length,
      staleInstances: executiveRows.filter((row) => row.staleMonthsOperational > 0).length
    },
    vicePresidencies,
    topAlerts,
    topAdvances,
    interventionVicepresidencies: vicePresidencies
      .filter((vp) => vp.criticalCount > 0 || vp.attentionCount > 0)
      .slice(0, 5),
    legitimateExceptions: allInstances
      .filter((row) => EXCLUDED_CAPTURE_STATUSES.has(row.operationalStatus))
      .slice(0, 5),
    pendingCapture: captureApplicable
      .filter((row) => !row.currentMonthCapture)
      .sort((left, right) => right.staleMonthsOperational - left.staleMonthsOperational)
      .slice(0, 5)
  };
};

export type OperationalLoadState = 'REMOTE_ERROR' | 'SESSION_EXPIRED' | 'NO_SCOPE';

export const classifyOperationalLoadError = (message: string): OperationalLoadState => {
  const normalized = String(message || '').toLowerCase();
  if (normalized.includes('expired') || normalized.includes('expirad') || normalized.includes('revoked') || normalized.includes('jwt')) {
    return 'SESSION_EXPIRED';
  }
  if (normalized.includes('scope') || normalized.includes('alcance') || normalized.includes('permission') || normalized.includes('403')) {
    return 'NO_SCOPE';
  }
  return 'REMOTE_ERROR';
};
